/* Todo Saludable motion layer — GSAP 3.13 + ScrollTrigger + SplitText + Lenis 1.3

   The site scrolls like a presentation: most motion is scrubbed, i.e. written from the
   scroll position on every frame, so it follows the scroll in both directions instead of
   firing once. Lenis smooths the wheel/trackpad; touch keeps the phone's native momentum.
   Things that are already on screen when a page opens (hero, product-page photos, bundle
   options) still get a one-shot entrance, since there is no scroll to drive them yet.
   Touch phones get quick one-shot reveals instead of scrubbing: writing transforms on every
   frame of a finger scroll made images stutter there. */
(function () {
  if (!window.gsap || !window.ScrollTrigger || !window.SplitText) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  gsap.registerPlugin(ScrollTrigger, SplitText);
  // The phone address bar showing/hiding resizes the viewport; don't re-measure for that.
  ScrollTrigger.config({ ignoreMobileResize: true });
  window.__tsMotion = true;

  var root = document.documentElement;
  var main = document.getElementById('MainContent');
  if (!main) { root.classList.remove('ts-motion'); return; }

  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };
  var EASE = 'power3.out';
  var pageStart = performance.now();

  /* ---------- Opening intro (first page of a visit): logo, tagline, then the curtain lifts ---------- */
  var intro = document.getElementById('ts-intro');
  var introLen = 0;
  if (intro && performance.now() > 2600) { intro.remove(); intro = null; } // scripts were slow; skip it
  if (intro) {
    introLen = 1.4;
    intro.style.animation = 'none';
    var tagChars = SplitText.create(intro.querySelector('.ts-intro__tag'), { type: 'chars' }).chars;
    gsap.timeline({ onComplete: function () { intro.remove(); } })
      .from('.ts-intro__logo', { y: 30, scale: 0.9, opacity: 0, duration: 0.6, ease: 'expo.out' })
      .from(tagChars, { yPercent: 120, opacity: 0, duration: 0.45, ease: EASE, stagger: 0.018 }, 0.12)
      .to('.ts-intro__bar i', { scaleX: 1, duration: 0.6, ease: 'power2.inOut' }, 0.15)
      .to('.ts-intro__inner', { y: -36, opacity: 0, duration: 0.35, ease: 'power2.in' }, 0.75)
      .to('.ts-intro__panel', { clipPath: 'inset(0% 0% 100% 0%)', duration: 0.65, ease: 'expo.inOut' }, 0.78);
  }

  var mm = gsap.matchMedia();

  // Horizon scrolls .page-wrapper on desktop (>=990px) and the window on smaller screens.
  mm.add({ desktop: '(min-width: 990px)', mobile: '(max-width: 989px)' }, function (ctx) {
    var desktop = ctx.conditions.desktop;
    var scroller = desktop ? $('.page-wrapper') : window;
    ScrollTrigger.defaults({ scroller: scroller });
    var scrollTop = function () { return desktop ? scroller.scrollTop : window.scrollY; };
    var touch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    /* ---------- Lenis: smooth wheel/trackpad scrolling at every width ---------- */
    var lenis = null, tick = null, lockObserver = null;
    if (window.Lenis && scroller) {
      var lenisOpts = {
        lerp: 0.09,
        anchors: true,
        stopInertiaOnNavigate: true,
        autoRaf: false,
        prevent: function (node) {
          return !!(node.closest && node.closest('dialog, details[open], [data-lenis-prevent], .drawer, cart-drawer-component, slideshow-container, .resource-list__carousel'));
        }
      };
      if (desktop) { lenisOpts.wrapper = scroller; lenisOpts.content = main; }
      lenis = new Lenis(lenisOpts);
      lenis.on('scroll', ScrollTrigger.update);
      tick = function (t) { lenis.raf(t * 1000); };
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);

      // Pause Lenis whenever Horizon locks scrolling (cart drawer, menus, modals).
      var syncLock = function () { root.hasAttribute('scroll-lock') ? lenis.stop() : lenis.start(); };
      lockObserver = new MutationObserver(syncLock);
      lockObserver.observe(root, { attributes: true, attributeFilter: ['scroll-lock'] });
      syncLock();
    }

    var travel = desktop ? 64 : 28;

    /* Quick one-shot reveal used on touch phones: plays once as items enter, in a fast
       left-to-right ripple per row. */
    var pop = function (els, from, to) {
      if (!els.length) return;
      gsap.set(els, from);
      ScrollTrigger.batch(els, {
        start: 'top 94%',
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, Object.assign({ y: 0, opacity: 1, duration: 0.55, ease: 'power3.out', stagger: 0.07, overwrite: true }, to));
        }
      });
    };

    /* Column index of each element within its visual row (elements sharing a top). Later
       columns start a little later, so a row arrives left to right; on a stacked phone
       column every item is its own row and arrives by itself. Measured before any
       transforms are written. */
    var rowCols = function (els) {
      var rows = {};
      return els.map(function (el) {
        var t = Math.round(el.getBoundingClientRect().top + scrollTop());
        var col = rows[t] || 0;
        rows[t] = col + 1;
        return { el: el, col: col };
      });
    };

    /* Scrubbed rise: travels up and fades from 20% to full as its top moves from the
       bottom of the screen to ~40% up it, and plays backwards when scrolling up. */
    var rise = function (els, extra) {
      if (touch) { pop(els, { y: travel, opacity: 0 }, { clearProps: 'transform,filter' }); return; }
      rowCols(els).forEach(function (it) {
        var off = Math.min(it.col, 3) * 12;
        gsap.fromTo(it.el, Object.assign({ y: travel, opacity: 0.2 }, extra && extra.from), Object.assign({
          y: 0, opacity: 1, ease: 'power2.out',
          scrollTrigger: { trigger: it.el, start: 'top ' + (100 - off) + '%', end: 'top ' + (58 - off) + '%', scrub: true }
        }, extra && extra.to));
      });
    };

    /* ---------- Hero: the banner drifts at 40% of scroll speed and pushes in; the copy
       below it lifts and fades on its way out ---------- */
    var hero = $('[id$="__banner_hero"]', main);
    var heroImg = hero && $('img', hero);
    if (heroImg) {
      // Opening: with the intro, the zoom starts as the curtain lifts so the hero settles into place.
      gsap.fromTo(heroImg,
        { opacity: 0, scale: introLen ? 1.18 : 1.08 },
        { opacity: 1, scale: 1, duration: introLen ? 1.8 : 1.4, ease: introLen ? 'expo.out' : 'power2.out',
          delay: introLen ? introLen - 0.75 : 0, clearProps: 'opacity' });
      // Parallax runs on the image's wrapper so it never fights the opening zoom above.
      if (!touch) gsap.to(heroImg.parentElement, {
        y: function () { return hero.offsetHeight * (desktop ? 0.4 : 0.2); },
        scale: 1.06, ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true, invalidateOnRefresh: true }
      });
    }
    var heroCopySec = $('[id$="__hero_intro"]', main);
    var heroCopy = heroCopySec && heroCopySec.firstElementChild;
    if (heroCopy) {
      // Headline rises letter by letter as the page opens.
      var heroHead = $('h1, h2', heroCopy);
      if (heroHead) {
        var hs = SplitText.create(heroHead, { type: 'words,chars', mask: 'words' });
        gsap.from(hs.chars, { yPercent: 115, duration: 0.9, ease: 'expo.out', stagger: 0.018, delay: introLen ? introLen - 0.4 : 0.2 });
      }
      if (!touch) gsap.to(heroCopy, {
        y: desktop ? -60 : -24, opacity: 0, ease: 'power1.in',
        scrollTrigger: { trigger: heroCopySec, start: 'top+=' + Math.round(heroCopySec.offsetHeight * 0.25) + ' top', end: 'bottom top', scrub: true }
      });
    }
    root.classList.remove('ts-motion');

    /* ---------- Marquee band: slides up into place ---------- */
    var marquee = $('[id$="__marquee_band"] .marquee__wrapper');
    if (marquee) {
      gsap.from(marquee, { yPercent: 100, opacity: 0, duration: 0.8, ease: EASE, delay: 0.3 + introLen });
    }

    /* ---------- Chips (✽ SHOP ✽ etc.): scale up with the scroll ---------- */
    $$('.text-block--background', main).forEach(function (chip) {
      if (heroCopy && heroCopy.contains(chip)) return;
      if (touch) { pop([chip], { scale: 0.8, opacity: 0 }, { scale: 1, ease: 'back.out(2)' }); return; }
      gsap.fromTo(chip, { scale: 0.7, opacity: 0 }, {
        scale: 1, opacity: 1, ease: 'back.out(2)',
        scrollTrigger: { trigger: chip, start: 'top 95%', end: 'top 70%', scrub: true }
      });
    });

    /* ---------- Section headings: letters rise through a mask, scrubbed (React Bits "SplitText") ---------- */
    $$('.text-block.h2 > p', main).forEach(function (p) {
      var s = SplitText.create(p, { type: 'words,chars', mask: 'words' });
      if (touch) {
        gsap.from(s.chars, { yPercent: 110, duration: 0.6, ease: 'power3.out', stagger: 0.015,
          scrollTrigger: { trigger: p, start: 'top 92%', once: true } });
        return;
      }
      gsap.from(s.chars, {
        yPercent: 110, opacity: 0, ease: 'power2.out', stagger: 0.03,
        scrollTrigger: { trigger: p, start: 'top 92%', end: 'top 60%', scrub: true }
      });
    });

    /* ---------- Body copy: rises out of a blur, scrubbed (React Bits "BlurText") ---------- */
    rise($$('[class*="__na_sub"], [class*="__dx_sub"], [class*="__rv_intro"], [class*="__pr_points"], [class*="__nl_body"]', main)
      .filter(function (el) { return !(heroCopy && heroCopy.contains(el)); }),
      { from: { filter: 'blur(6px)' }, to: { filter: 'blur(0px)' } });

    /* ---------- Product cards: a scrubbed wave. Each card rises (later columns a beat
       later), its photo wipes up from the bottom and settles from a slight zoom ---------- */
    var cardsIn = function (cards) {
      cards = cards.filter(function (c) { return !c.__tsCard; });
      if (!cards.length) return;
      cards.forEach(function (c) { c.__tsCard = true; });
      if (touch) {
        // Phones: the card rises and its photo wipes open once, quickly, no per-frame work.
        pop(cards, { y: 32, opacity: 0 }, { clearProps: 'transform' });
        return;
      }
      rowCols(cards).forEach(function (it) {
        var off = Math.min(it.col, 3) * 12;
        var st = function () {
          return { trigger: it.el, start: 'top ' + (102 - off) + '%', end: 'top ' + (55 - off) + '%', scrub: true };
        };
        gsap.fromTo(it.el, { y: travel * 1.5, rotate: 1.5, transformOrigin: '50% 100%' },
          { y: 0, rotate: 0, ease: 'power2.out', scrollTrigger: st() });
        var gal = $('.card-gallery', it.el);
        if (gal) {
          gsap.fromTo(gal, { clipPath: 'inset(100% 0% 0% 0% round 12px)' },
            { clipPath: 'inset(0% 0% 0% 0% round 12px)', ease: 'power2.out', scrollTrigger: st() });
          var img = $('img', gal);
          if (img) gsap.fromTo(img, { scale: 1.25 }, { scale: 1, ease: 'power2.out', scrollTrigger: st() });
        }
      });
    };
    cardsIn($$('product-card', main));
    // Cards added after load (recommendations, filtering, sorting) get the same treatment.
    var cardObserver = new MutationObserver(function (muts) {
      var fresh = [];
      muts.forEach(function (mu) {
        mu.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.matches('product-card')) fresh.push(n);
          fresh.push.apply(fresh, $$('product-card', n));
        });
      });
      if (fresh.length) { cardsIn(fresh); ScrollTrigger.refresh(); }
    });
    cardObserver.observe(main, { childList: true, subtree: true });

    /* ---------- One-shot entrances for things on screen as a page opens ---------- */
    var introDelay = function () { return Math.max(0, 0.55 + introLen - (performance.now() - pageStart) / 1000); };
    var enter = function (els, from) {
      if (!els.length) return;
      gsap.from(els, Object.assign({ opacity: 0, y: 50, scale: 0.97, duration: 1.1, ease: 'expo.out', stagger: 0.12, delay: introDelay(), clearProps: 'transform' }, from));
    };
    // Product page: photos and bundle options.
    enter($$('media-gallery .product-media-container', main).filter(function (el) { return !el.closest('dialog, zoom-dialog'); }).slice(0, 4));
    enter($$('.ts-bundle__opt', main), { y: 0, x: 40, scale: 1 });
    // Ad landing pages: hero photo and offers.
    enter($$('.tsl-hero__media, .tsl-offer', main));
    // Landing-page benefits, FAQ and guarantee follow the scroll.
    rise($$('.tsl-content ul li, .tsl-content details, .tsl-content h2, .tsl-guarantee .page-width > *', main));

    /* ---------- Every other page (about, guarantee, contact, blog...): sections rise with the scroll ---------- */
    if (!hero) {
      rise($$('.shopify-section', main).slice(1).filter(function (sec) {
        return !sec.querySelector('product-card, [data-tsl], product-recommendations');
      }));
    }

    /* ---------- Intense Detox feature: the photo wipes open and drifts inside its frame ---------- */
    var detox = $('[id$="__detox_feature"]');
    if (detox) {
      var media = $('.media-block', detox);
      var dimg = media && $('img', media);
      if (media && touch) {
        gsap.fromTo(media, { clipPath: 'inset(10% 10% 10% 10% round 24px)' }, {
          clipPath: 'inset(0% 0% 0% 0% round 0px)', duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: detox, start: 'top 85%', once: true }
        });
      } else if (media) {
        gsap.fromTo(media, { clipPath: 'inset(14% 14% 14% 14% round 24px)' }, {
          clipPath: 'inset(0% 0% 0% 0% round 0px)', ease: 'power2.out',
          scrollTrigger: { trigger: detox, start: 'top 90%', end: 'top 30%', scrub: true }
        });
      }
      if (dimg && !touch) {
        gsap.fromTo(dimg, { scale: 1.18, yPercent: -7 }, {
          scale: 1.18, yPercent: 7, ease: 'none',
          scrollTrigger: { trigger: detox, start: 'top bottom', end: 'bottom top', scrub: true }
        });
      }
      rise($$('.button, .button-secondary', detox));
    }

    /* ---------- Guarantee + newsletter + "Shop all" button rise with the scroll ---------- */
    rise($$('[id$="__promise_section"] .button-secondary, [id$="__shop_all_cta"] .button-secondary, [id$="__newsletter_band"] form', main));

    /* ---------- Magnetic buttons (React Bits "Magnet"), desktop pointer only ---------- */
    var magnetCleanups = [];
    if (desktop && window.matchMedia('(hover: hover)').matches) {
      $$('.button, .button-secondary, .button-custom', main).forEach(function (btn) {
        var xTo = gsap.quickTo(btn, 'x', { duration: 0.4, ease: 'power3' });
        var yTo = gsap.quickTo(btn, 'y', { duration: 0.4, ease: 'power3' });
        var move = function (e) {
          var r = btn.getBoundingClientRect();
          xTo((e.clientX - (r.left + r.width / 2)) * 0.25);
          yTo((e.clientY - (r.top + r.height / 2)) * 0.35);
        };
        var leave = function () { xTo(0); yTo(0); };
        btn.addEventListener('pointermove', move);
        btn.addEventListener('pointerleave', leave);
        magnetCleanups.push(function () {
          btn.removeEventListener('pointermove', move);
          btn.removeEventListener('pointerleave', leave);
        });
      });
    }

    // Images that load late change layout — re-measure trigger positions.
    window.addEventListener('load', function () { ScrollTrigger.refresh(); }, { once: true });

    return function () {
      magnetCleanups.forEach(function (fn) { fn(); });
      if (lockObserver) lockObserver.disconnect();
      cardObserver.disconnect();
      if (tick) gsap.ticker.remove(tick);
      if (lenis) lenis.destroy();
    };
  });
})();
